import React, { useEffect, useRef, useState } from 'react';
import { Styles } from '@t2c/shared-fe';
import Typography from '@mui/material/Typography';
import { NextImage, RHFUploadSingleFile } from '@sdf/base';
import { CircularProgress, Divider, Slider, TextField, Tooltip } from '@mui/material';
import { Box, IconButton, InputAdornment } from '@mui/material';
import { useSnackbar } from 'notistack';
import { usePresignedUrlUpload, useUploadId } from '../../../../services';
import { FileUploader } from 'react-drag-drop-files';
import { generatePreSignedUrl } from 'apps/t2c-fe/services/upload/generatePreSignedUrl';
import { getCustomBrolls } from 'apps/t2c-fe/services/custom-broll/getCustomBrolls';
import { useUploadBroll } from 'apps/t2c-fe/services/custom-broll/uploadBroll';
import { MediaStatus, RECENT_UPLOADS_LIMIT, REFETCH_INTERVAL, UploadTypes } from 'apps/t2c-fe/utils';
import DeleteIcon from '@mui/icons-material/Delete';
import { deleteCustomBrolls } from 'apps/t2c-fe/services/custom-broll/deleteCustomBroll';
import { VideoPlayerForStockVideos } from 'apps/t2c-fe/components/video-player/VideoPlayerForStockVideos';
import { BrollMedia } from '../../../../../t2c-fe/@types';
import { extractFileMetadata } from 'apps/t2c-fe/services/upload/extractFileMetadata';

interface Props {
  isImage: boolean;
  customBrollData: Array<BrollMedia>;
  setCustomBrollData: (_) => void;
  handleItemClick: (index: number) => void;
  selectedIndex: number;
  pageSize: number;
  setCustomBrollCount: (_) => void;
  searchText: string;
}

export const CustomBRolls: React.FC<Props> = ({
  isImage,
  customBrollData,
  setCustomBrollData,
  handleItemClick,
  selectedIndex,
  pageSize,
  setCustomBrollCount,
  searchText,
}) => {
  const [uploadURL, setUploadURL] = useState('');
  const [fileDetails, setFileDetails] = useState<Record<string, string>>(null);
  const abortController = useRef(null);
  const { mutateAsync: fetchUploadId } = useUploadId();
  const [uploading, setUploading] = useState(false);
  const [firstPageData, setFirstPageData] = useState({ data: [], count: null });
  const [customRecentBrollCount, setCustomRecentBrollCount] = useState(null);

  const { mutateAsync: uploadToPresignedUrl } = usePresignedUrlUpload({
    onUploadProgress: (percentCompleted) => {
      // setUploadPercentage(percentCompleted);
    },
  });
  const { mutateAsync: uploadBroll } = useUploadBroll();

  const handleFileSelect = (file) => {
    if (file.size > 10737418240) {
      enqueueSnackbar('File size exceeds 1GB. Please choose a smaller file.', {
        variant: 'error',
      });
      return;
    }
    if (!isImage) {
      const videoElement = document.createElement('video');
      videoElement.preload = 'metadata';

      videoElement.onloadedmetadata = () => {
        window.URL.revokeObjectURL(videoElement.src);
        if (videoElement.duration > 1800) {
          enqueueSnackbar('Video duration exceeds 30 minutes. Please choose a shorter video.', {
            variant: 'error',
          });
          return;
        }

        const filePreview = URL.createObjectURL(file);
        setFileDetails(file);
        setUploadURL(filePreview);
        handleFileUpload(file);
      };

      videoElement.src = URL.createObjectURL(file);
    } else {
      const filePreview = URL.createObjectURL(file);
      setFileDetails(file);
      setUploadURL(filePreview);
      handleFileUpload(file);
    }
  };

  const handleFileUpload = async (file) => {
    setUploading(true);
    try {
      abortController.current = new AbortController();
      const { signal } = abortController.current;

      const { uploadId: multiPartUploadId, preSignedUrls, format } = await generatePreSignedUrl(file);
      await uploadToPresignedUrl({
        uploadId: multiPartUploadId, 
        presignedUrls: preSignedUrls,
        file: file,
        signal: signal,
      });

      const metadata = await extractFileMetadata(file, format);

      const uploadRes = await fetchUploadId({
        url: preSignedUrls[0].url,
        format,
        mimeType: file.type,
        signal,
        metadata,
      });
      await uploadBroll({ uploadId: uploadRes.uploadId, name: file.name });
      await fetchCustomBrolls();
    } catch (err) {
      console.error(err);
      throw err;
    } finally {
      abortController.current = null;
    }
    setUploading(false);
  };

  const fetchCustomBrolls = async () => {
    const data = await getCustomBrolls({
      type: isImage ? UploadTypes.Image : UploadTypes.Video,
      limit: pageSize,
    });
    setCustomBrollData(data?.[0]?.brollMedia);
    setCustomBrollCount(data?.[0]?.count);
    setCustomRecentBrollCount(data?.[0]?.recentCount);

    if (pageSize === 10) setFirstPageData({ data: data[0]?.brollMedia, count: data[0]?.count });
  };

  const { enqueueSnackbar } = useSnackbar();
  useEffect(() => {
    fetchCustomBrolls();
  }, [pageSize]);

  useEffect(() => {
    const isInProgress = customBrollData?.some(
      (item) => item.status === MediaStatus.NotStarted || item.status === MediaStatus.InProgress
    );

    let intervalID: NodeJS.Timeout | undefined;

    if (isInProgress) {
      intervalID = setTimeout(() => {
        fetchCustomBrolls();
      }, REFETCH_INTERVAL.CUSTOM_BROLL);
    }

    return () => {
      if (intervalID) {
        clearTimeout(intervalID);
      }
    };
  }, [customBrollData]);

  const supportedFormats = isImage ? ['jpg', 'jpeg', 'png'] : ['mp4'];

  const handleDelete = async (id) => {
    await deleteCustomBrolls({ id });
    setCustomBrollData((prev) => [...prev.filter((res) => res?.id !== id)]);
    enqueueSnackbar('Deleted!', { variant: 'success' });
  };

  useEffect(() => {
    if (!searchText && !!firstPageData.data.length) {
      setCustomBrollData(firstPageData.data);
      setCustomBrollCount(firstPageData.count);
    }
  }, [searchText]);

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <FileUploader
          multiple={false}
          handleChange={(file) => {
            if (customRecentBrollCount >= RECENT_UPLOADS_LIMIT) {
              enqueueSnackbar('You have reached the upload limit. For support, contact support@txt2create.com.', {
                variant: 'error',
              });
              return;
            }
            handleFileSelect(file)
          }}
          name="file"
          types={supportedFormats}
          onTypeError={(err) => {
            enqueueSnackbar('Unsupported file type. Please upload supported file format', {
              variant: 'error',
            });
          }}
          disabled={uploading}
        >
          <Box sx={styles.uploader}>
            <Typography fontSize="14px" sx={styles.uploaderText}>
              Click or <br /> Drag/Drop <br /> to upload file
            </Typography>
            <Typography
              fontSize={'12px'}
              sx={{ color: (theme) => theme.palette.text.modalLightText }}
            >
              <br /> {supportedFormats.join('/ ')}
            </Typography>
          </Box>
        </FileUploader>
        {uploading && (
          <Box sx={styles.uploading}>
            <Box sx={{ borderWidth: 1, borderRadius: '10px' }}>
              {isImage ? (
                <NextImage
                  src={uploadURL}
                  alt="Logo"
                  style={{
                    width: '80%',
                    objectFit: 'contain',
                    margin: 'auto',
                  }}
                  width={234}
                  height={200}
                />
              ) : (
                <video key={uploadURL} width={'100%'} height={200}>
                  <source src={uploadURL} type="video/mp4" />
                  <source src={uploadURL} type="video/quicktime" />
                  Your browser does not support this video
                </video>
              )}
            </Box>
            {uploading && (
              <CircularProgress
                size={25}
                sx={{
                  position: 'absolute',
                  left: '40%',
                  top: '40%',
                }}
              />
            )}
          </Box>
        )}

        {customBrollData.map((res, index) => (
          <Tooltip sx={{ color: 'white' }} title={res.name} key={index}>
            <Box
              key={index}
              onClick={() => {
                if (res.status === MediaStatus.Success) handleItemClick(index);
              }}
              sx={{ ...styles.card, borderWidth: selectedIndex === index ? 4 : 0 }}
            >
              {[MediaStatus.InProgress, MediaStatus.NotStarted].includes(res.status) && (
                <Typography sx={{ color: (theme) => theme.palette.common.white }}>
                  In Progress...
                </Typography>
              )}
              {res.status === MediaStatus.Error && (
                <Typography sx={{ color: (theme) => theme.palette.text.error }}>Failed!</Typography>
              )}

              {res.uploads.type === 'Video' && res.status === MediaStatus.Success && (
                <Box sx={{ width: '100%' }}>
                  <VideoPlayerForStockVideos height="142px" url={res?.uploads?.url} />
                </Box>
              )}

              {res.uploads.type === 'Image' && res.status === MediaStatus.Success && (
                <Box sx={{ height: '100%', width: '100%' }}>
                  <img
                    src={res?.thumbnail}
                    alt=""
                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                    //  height={200} width={150}
                  />
                </Box>
              )}

              <DeleteIcon
                color="disabled"
                sx={{
                  position: 'absolute',
                  top: 1,
                  right: 1,
                  '&:hover': { color: (theme) => theme.palette.background.deleteBtn },
                }}
                onClick={() => handleDelete(res.id)}
              />
            </Box>
          </Tooltip>
        ))}
      </Box>
    </Box>
  );
};

const styles: Styles = {
  modal: { width: '800px', margin: 'auto', height: '700px' },
  title: {
    padding: '24px',
    paddingTop: '10px',
  },
  subTitle: {
    fontSize: '14px',
    fontWeight: 400,
    color: (theme) => theme.palette.text.secondary,
  },
  dialogContent: {
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  theme: {
    width: '128px',
    paddingY: 2,
    backgroundColor: (theme) => theme.palette.background.selectedCardBorder,
    color: (theme) => theme.palette.common.white,
    textAlign: 'center',
  },
  clipsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 2,
    maxHeight: '350px',
    minHeight: '350px',
    overflowY: 'auto',
  },
  saveBtn: {
    color: (theme) => theme.palette.common.white,
    boxShadow: 'none',
    width: '69px',
    height: '38px',
    borderRadius: '20px',
    fontWeight: '500',
  },
  cancelBtn: {
    color: (theme) => theme.palette.common.black,
    borderColor: (theme) => theme.palette.background.dark,
    width: '69px',
    height: '38px',
    borderRadius: '20px',
    fontWeight: '500',
  },
  btns: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 1,
  },
  input: {
    backgroundColor: (theme) => theme.palette.background.card,
    marginBottom: 3,
  },
  closeButton: {
    color: (theme) => theme.palette.grey[500],
    mr: -1,
  },
  typeTab: {
    display: 'flex',
    borderRadius: '6px',
    borderColor: (theme) => theme.palette.background.cardBorder,
    justifyContent: 'space-between',
    borderWidth: '1px',
  },
  tab: {
    flex: 1,
    textAlign: 'center',
    paddingY: 1,
    borderColor: (theme) => theme.palette.background.cardBorder,
    cursor: 'pointer',
  },
  uploaderText: {
    color: (theme) => theme.palette.text.coloredText,
  },
  uploader: {
    height: 200,
    width: '210px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    textAlign: 'center',
    borderStyle: 'dashed',
    borderRadius: '10px',
    flexDirection: 'column',
    borderColor: (theme) => theme.palette.text.coloredText,
    cursor: 'pointer',
    // backgroundColor: (theme) => theme.palette.background.modal,
  },
  card: {
    height: 200,
    width: 210,
    borderWidth: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderColor: (theme) => theme.palette.text.coloredText,
    backgroundColor: (theme) => theme.palette.common.black,
    borderRadius: '10px',
    cursor: 'pointer',
    overflow: 'hidden',
    position: 'relative',
  },
  uploading: {
    position: 'relative',
    backgroundColor: (theme) => theme.palette.common.black,
    borderRadius: '10px',
    width: '210px',
  },
};
